import type { Campaign } from 'c-sdk';

/**
 * The decisions behind the Pop Settings form: what it shows for a stored value and what it sends.
 * The API side is `CampaignPopSettingsUpdateRequest` (uniliveapi).
 */

export type AudienceVersionId = number;

/** `CampaignRefreshOptions` in the API, in the order the select lists them. */
export const PCA_END_SETTINGS = ['now', 'two_days_ago', 'custom', 'based_on_owners'] as const;

/**
 * Log sources whose campaign identifier is also added as a campaign media "name" parameter
 * (`<identifier>_<campaignId>`) when it is new.
 */
export const NAME_PARAMETER_LOG_SOURCES = [2, 4];

export type LogSourceIdentifierRow = { source: number | null; identifier: string };

/** Positive whole ids only, each once, in the order chosen (the order is the priority). */
export const sanitizeVersionIds = (
    ids?: (AudienceVersionId | null | undefined)[] | null,
): AudienceVersionId[] =>
    Array.from(
        new Set(
            (ids ?? []).filter(
                (v): v is AudienceVersionId => typeof v === 'number' && Number.isFinite(v) && v > 0,
            ),
        ),
    );

/** The ids the form starts with: the campaign's list, else the versions it has loaded. */
export const initialAudienceVersionIds = (
    campaign: Pick<Campaign, 'audience_version_ids' | 'audienceVersions'> | null | undefined,
): AudienceVersionId[] => {
    if (campaign?.audience_version_ids?.length)
        return sanitizeVersionIds(campaign.audience_version_ids);
    if (campaign?.audienceVersions?.length)
        return sanitizeVersionIds(campaign.audienceVersions.map(v => v.id));
    return [];
};

/** Drag-and-drop reorder of the chosen versions. */
export const moveVersionId = (ids: AudienceVersionId[], from: number, to: number) => {
    if (from === to) return ids;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};

/**
 * A picked version goes to the end: picking never re-sorts the list, so the primary only changes
 * when the user moves it. Picking one already chosen changes nothing.
 */
export const appendVersionId = (ids: AudienceVersionId[], id: AudienceVersionId) =>
    ids.includes(id) ? ids : [...ids, id];

/** Removing a version closes the gap; everything else keeps its relative order. */
export const removeVersionId = (ids: AudienceVersionId[], id: AudienceVersionId) =>
    ids.filter(v => v !== id);

/** A stored PCA datetime split into the date the picker shows and the time the form keeps. */
export const splitPcaDateTime = (value: string | null | undefined) => {
    if (!value) return { datePart: null as string | null, timePart: '00:00:00' };

    const [datePart, timeWithZone = '00:00:00'] = value.split('T');
    return { datePart, timePart: timeWithZone.split('+')[0].split('-')[0] };
};

/**
 * The picked date (the date field gives `yyyy-MM-dd`) joined to the time already held.
 * Read through `new Date(...)` and its local getters, as the form does.
 */
export const pcaDateTimeWithDate = (pickedDate: string | null, timePart: string) => {
    if (!pickedDate) return null;

    const d = new Date(pickedDate);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${timePart}`;
};

/** The time input's `HH:mm` joined to the date already held. */
export const pcaDateTimeWithTime = (datePart: string | null, time: string) =>
    datePart ? `${datePart}T${time}:00` : null;

/** The multiplier field only accepts digits with at most one decimal point. */
export const isImpressionMultiplierInput = (value: string) => /^\d*\.?\d*$/.test(value);

/** The multiplier is saved on the campaign itself, separately, and only when it changed. */
export const impressionMultiplierUpdate = (
    current: number | string | null | undefined,
    submitted: number | string | null | undefined,
) =>
    current !== submitted
        ? { impression_multiplier: parseFloat(String(submitted)).toFixed(10) }
        : null;

/** A custom end date is only sent with the custom end setting. */
export const withoutUnusedPcaEnd = <
    T extends { pca_end_setting?: string | null; pca_end_datetime?: unknown },
>(
    data: T,
): T => {
    const payload = { ...data };
    if (payload.pca_end_setting !== 'custom') delete payload.pca_end_datetime;
    return payload;
};

export const logSourceRowsFromCampaign = (
    identifiers: Campaign['logSourceCampaignIdentifiers'] | null | undefined,
): LogSourceIdentifierRow[] =>
    identifiers?.map(identifier => ({
        source: identifier.log_source_account_id,
        identifier: identifier.identifier,
    })) ?? [];

/** New identifiers on name-parameter sources, as the media "name" values to add. */
export const logSourceNamesToAdd = (
    rows: { source?: number | null; identifier?: string }[] | null | undefined,
    existing: LogSourceIdentifierRow[],
    campaignId: number,
) =>
    (rows ?? []).reduce((names, { source, identifier }) => {
        if (source != null && NAME_PARAMETER_LOG_SOURCES.includes(source)) {
            const alreadySaved = existing.some(
                item => item.source === source && item.identifier === identifier,
            );
            if (!alreadySaved) names.push(`${identifier}_${campaignId}`);
        }
        return names;
    }, [] as string[]);
